import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { headerApi, siteHeaderApi, assetUrl } from '../services/api';

export interface PreloaderProps {
  onLoadingComplete: () => void;
  initialSettings?: any;
  waitForExternal?: boolean;
}

export const Preloader: React.FC<PreloaderProps> = ({ onLoadingComplete, initialSettings, waitForExternal = false }) => {
  const [loading, setLoading] = useState(true);
  const [showText, setShowText] = useState(false);
  const [complete, setComplete] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    // Fetch logo URL
    const fetchLogo = async () => {
      try {
        let headerRes: any = null;
        try {
          headerRes = await headerApi.get();
        } catch (err) {
          headerRes = await siteHeaderApi.get();
        }
        if (headerRes?.success && headerRes.data?.logo_url) {
          setLogoUrl(headerRes.data.logo_url);
        }
      } catch (err) {
        console.error('Error fetching logo:', err);
      }
    };
    fetchLogo();
  }, []);

  useEffect(() => {
    // Loading phase
    const loadingTimer = setTimeout(() => {
      setLoading(false);
      setShowText(true);
    }, 1500);

    // Text display phase - increased to 5 seconds total
    const textTimer = setTimeout(() => {
      setShowText(false);
      setComplete(true);
      onLoadingComplete();
    }, 5000);

    return () => {
      clearTimeout(loadingTimer);
      clearTimeout(textTimer);
    };
  }, [onLoadingComplete]);

  if (complete) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden bg-gray-900">
      <AnimatePresence mode="wait">
        {/* Loading Spinner Phase */}
        {loading && (
          <motion.div
            key="loader"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.2 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col items-center justify-center"
          >
            {/* Modern Amber Spinner */}
            <div className="relative w-20 h-20 mb-8">
              <div className="absolute inset-0 border-4 border-[#FFBF00]/20 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-t-[#FFBF00] border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
              <div className="absolute inset-2 border-2 border-[#FFBF00]/10 rounded-full"></div>
              <div className="absolute inset-2 border-2 border-t-[#FFBF00]/50 border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin animation-delay-150"></div>
            </div>
            
            {/* Loading Text */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-[#FFBF00] text-lg font-light tracking-wider"
            >
              LOADING...
            </motion.div>
          </motion.div>
        )}

        {/* Logo Animation Phase */}
        {showText && (
          <motion.div
            key="logo"
            initial={{ opacity: 0, y: 100, scale: 0.5 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{
              opacity: 0,
              y: -100,
              scale: 1.2,
              transition: { duration: 0.8, ease: "easeInOut" }
            }}
            transition={{
              duration: 1.2,
              ease: "easeOut",
              type: "spring",
              stiffness: 100,
              damping: 15
            }}
            className="text-center"
          >
            {/* Logo with Animation */}
            <motion.div
              initial={{ rotate: -180, scale: 0 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{
                delay: 0.3,
                duration: 1,
                type: "spring",
                stiffness: 200,
                damping: 20
              }}
              className="mb-6"
            >
              {logoUrl ? (
                <motion.img
                  src={assetUrl(logoUrl)}
                  alt="Soltechy Logo"
                  className="h-24 w-auto object-contain"
                  animate={{
                    scale: [1, 1.1, 1],
                    filter: [
                      'brightness(1)',
                      'brightness(1.2) drop-shadow(0 0 20px rgba(255, 191, 0, 0.5))',
                      'brightness(1)'
                    ]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatDelay: 1
                  }}
                />
              ) : (
                <motion.h1
                  className="text-[#FFBF00] font-black italic"
                  style={{ fontSize: 'clamp(3rem, 8vw, 6rem)' }}
                  initial={{ letterSpacing: '0.5em' }}
                  animate={{ letterSpacing: '0.2em' }}
                  transition={{ delay: 0.5, duration: 0.8 }}
                >
                  SOLTECHY
                </motion.h1>
              )}
            </motion.div>

            {/* Decorative Line */}
            <motion.div
              className="w-0 h-1 bg-[#FFBF00] mx-auto mt-6"
              animate={{ width: '100px' }}
              transition={{ delay: 0.8, duration: 0.8 }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Animated Gradient Orbs */}
        <motion.div
          className="absolute top-0 left-0 w-96 h-96 bg-[#FFBF00]/10 rounded-full blur-3xl"
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-0 right-0 w-96 h-96 bg-[#FFBF00]/10 rounded-full blur-3xl"
          animate={{
            x: [0, -100, 0],
            y: [0, 50, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        />
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="h-full w-full" style={{
            backgroundImage: `
              linear-gradient(rgba(255, 191, 0, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 191, 0, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
          }}></div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .animation-delay-150 {
          animation-delay: 150ms;
        }
      `}</style>
    </div>
  );
};